import type {
  CreateWhatsAppTemplateRequest,
  EditWhatsAppTemplateRequest,
  WhatsAppTemplateButtonInput,
  WhatsAppTemplateCategory,
  WhatsAppTemplateComponentsInput,
  WhatsAppTemplateDto,
  WhatsAppTemplateExamplesInput,
  WhatsAppTemplateManageParameterFormat,
  WhatsAppTemplatePresentation,
} from "@multi-chat/shared";
import { isValidWhatsAppTemplatePhoneNumber } from "@multi-chat/shared";

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
const NAMED_PLACEHOLDER_RE = /^[a-z][a-z0-9_]*$/;

export type TemplateButtonFormState =
  | { type: "QUICK_REPLY"; clientId?: string; text: string }
  | { type: "URL"; clientId?: string; text: string; url: string; example?: string }
  | { type: "PHONE_NUMBER"; clientId?: string; text: string; phoneNumber: string }
  | { type: "VOICE_CALL"; clientId?: string; text: string; ttlMinutes?: number };

export function createTemplateFormButtonClientId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `btn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface TemplateFormState {
  name: string;
  language: string;
  category: WhatsAppTemplateCategory;
  parameterFormat: WhatsAppTemplateManageParameterFormat;
  hasHeader: boolean;
  headerKind: "NONE" | "TEXT" | "IMAGE";
  headerText: string;
  headerImageFile: File | null;
  headerImagePreviewUrl: string | null;
  hasStoredHeaderImage: boolean;
  bodyText: string;
  hasFooter: boolean;
  footerText: string;
  buttons: TemplateButtonFormState[];
  headerExample: string;
  bodyExamples: Record<string, string>;
  urlButtonExamples: Record<number, string>;
}

export type TemplateFormValidationIssue =
  | { field: "parameterFormat"; code: "mixed" | "mismatch" }
  | { field: "header"; code: "mismatch" | "tooManyVariables" | "imageRequired" }
  | { field: "body"; code: "required" | "mismatch" | "dangling" }
  | { field: "examples"; code: "missing"; key: string }
  | { field: "buttons"; code: "interleaved" }
  | { field: "buttons"; code: "duplicateQuickReply" }
  | { field: "buttons"; code: "invalidPhone"; buttonIndex: number }
  | { field: "buttons"; code: "invalidUrl"; buttonIndex: number };

export function createEmptyTemplateFormState(): TemplateFormState {
  return {
    name: "",
    language: "he",
    category: "UTILITY",
    parameterFormat: "POSITIONAL",
    hasHeader: false,
    headerKind: "NONE",
    headerText: "",
    headerImageFile: null,
    headerImagePreviewUrl: null,
    hasStoredHeaderImage: false,
    bodyText: "",
    hasFooter: false,
    footerText: "",
    buttons: [],
    headerExample: "",
    bodyExamples: {},
    urlButtonExamples: {},
  };
}

/** Comparable editor draft. Preview URLs are omitted so a loaded header image does not count as an edit. */
export function templateEditorDraftSignature(state: TemplateFormState): string {
  return JSON.stringify({
    name: state.name,
    language: state.language,
    category: state.category,
    parameterFormat: state.parameterFormat,
    hasHeader: state.hasHeader,
    headerKind: state.headerKind,
    headerText: state.headerText,
    headerImage:
      state.headerImageFile == null
        ? ""
        : `${state.headerImageFile.name}\0${state.headerImageFile.size}`,
    bodyText: state.bodyText,
    hasFooter: state.hasFooter,
    footerText: state.footerText,
    buttons: state.buttons,
    headerExample: state.headerExample,
    bodyExamples: state.bodyExamples,
    urlButtonExamples: state.urlButtonExamples,
  });
}

export function isTemplateEditorDraftDirty(
  current: TemplateFormState,
  baseline: TemplateFormState,
): boolean {
  return templateEditorDraftSignature(current) !== templateEditorDraftSignature(baseline);
}

export function extractPlaceholderKeys(text: string): string[] {
  const keys: string[] = [];
  PLACEHOLDER_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
    keys.push(match[1]!);
  }
  return keys;
}

export function classifyPlaceholderKeys(
  keys: readonly string[],
): "NONE" | "POSITIONAL" | "NAMED" | "MIXED" {
  if (keys.length === 0) return "NONE";
  const allPositional = keys.every((key) => /^\d+$/.test(key));
  const allNamed = keys.every((key) => !/^\d+$/.test(key));
  if (allPositional) return "POSITIONAL";
  if (allNamed) return "NAMED";
  return "MIXED";
}

export function discoverFormVariables(state: TemplateFormState): {
  headerKeys: string[];
  bodyKeys: string[];
  urlButtonKeys: { buttonIndex: number; keys: string[] }[];
} {
  const headerKeys =
    state.headerKind !== "IMAGE" && state.hasHeader
      ? extractPlaceholderKeys(state.headerText)
      : [];
  const bodyKeys = extractPlaceholderKeys(state.bodyText);
  const urlButtonKeys = state.buttons.flatMap((button, buttonIndex) => {
    if (button.type !== "URL") return [];
    return [{ buttonIndex, keys: extractPlaceholderKeys(button.url) }];
  });
  return { headerKeys, bodyKeys, urlButtonKeys };
}

export function pruneStaleExamples(state: TemplateFormState): TemplateFormState {
  const { headerKeys, bodyKeys, urlButtonKeys } = discoverFormVariables(state);

  const headerExample = headerKeys.length === 1 ? state.headerExample : "";

  const bodyExamples: Record<string, string> = {};
  for (const key of bodyKeys) {
    const value = state.bodyExamples[key];
    if (value) bodyExamples[key] = value;
  }

  const urlButtonExamples: Record<number, string> = {};
  for (const entry of urlButtonKeys) {
    if (entry.keys.length === 1) {
      const fromButton = state.buttons[entry.buttonIndex];
      const fromButtonExample =
        fromButton?.type === "URL" ? fromButton.example?.trim() : undefined;
      const value = fromButtonExample || state.urlButtonExamples[entry.buttonIndex];
      if (value) urlButtonExamples[entry.buttonIndex] = value;
    }
  }

  return {
    ...state,
    headerExample,
    bodyExamples,
    urlButtonExamples,
  };
}

export function validateTemplateForm(state: TemplateFormState): TemplateFormValidationIssue[] {
  const issues: TemplateFormValidationIssue[] = [];
  if (!state.bodyText.trim()) {
    issues.push({ field: "body", code: "required" });
  }

  const { headerKeys, bodyKeys, urlButtonKeys } = discoverFormVariables(state);
  const allKeys = [...headerKeys, ...bodyKeys, ...urlButtonKeys.flatMap((entry) => entry.keys)];
  const classification = classifyPlaceholderKeys(allKeys);
  if (classification === "MIXED") {
    issues.push({ field: "parameterFormat", code: "mixed" });
  } else if (
    classification === "POSITIONAL" &&
    state.parameterFormat !== "POSITIONAL"
  ) {
    issues.push({ field: "parameterFormat", code: "mismatch" });
  } else if (classification === "NAMED" && state.parameterFormat !== "NAMED") {
    issues.push({ field: "parameterFormat", code: "mismatch" });
  }

  if (
    state.headerKind === "IMAGE" &&
    !state.headerImageFile &&
    !state.headerImagePreviewUrl &&
    !state.hasStoredHeaderImage
  ) {
    issues.push({ field: "header", code: "imageRequired" });
  }
  if (headerKeys.length > 1) {
    issues.push({ field: "header", code: "tooManyVariables" });
  }
  if (
    headerKeys.length > 0 &&
    ((state.parameterFormat === "NAMED" && headerKeys.some((key) => !NAMED_PLACEHOLDER_RE.test(key))) ||
      (state.parameterFormat === "POSITIONAL" && headerKeys.some((key) => !/^\d+$/.test(key))))
  ) {
    issues.push({ field: "header", code: "mismatch" });
  }
  if (
    bodyKeys.length > 0 &&
    ((state.parameterFormat === "NAMED" && bodyKeys.some((key) => !NAMED_PLACEHOLDER_RE.test(key))) ||
      (state.parameterFormat === "POSITIONAL" && bodyKeys.some((key) => !/^\d+$/.test(key))))
  ) {
    issues.push({ field: "body", code: "mismatch" });
  }

  if (headerKeys.length === 1 && !state.headerExample.trim()) {
    issues.push({ field: "examples", code: "missing", key: headerKeys[0]! });
  }

  for (const key of bodyKeys) {
    if (!state.bodyExamples[key]?.trim()) {
      issues.push({ field: "examples", code: "missing", key });
    }
  }

  for (const entry of urlButtonKeys) {
    const urlButton = state.buttons[entry.buttonIndex];
    const exampleValue =
      (urlButton?.type === "URL" ? urlButton.example : undefined) ??
      state.urlButtonExamples[entry.buttonIndex];
    if (entry.keys.length === 1 && !exampleValue?.trim()) {
      issues.push({ field: "examples", code: "missing", key: String(entry.buttonIndex) });
    }
    if (entry.keys.length > 1) {
      issues.push({ field: "parameterFormat", code: "mixed" });
    }
    for (const key of entry.keys) {
      if (!/^\d+$/.test(key)) {
        issues.push({ field: "parameterFormat", code: "mismatch" });
      }
    }
  }

  for (const key of bodyKeys) {
    if (state.parameterFormat === "NAMED" && !NAMED_PLACEHOLDER_RE.test(key)) {
      issues.push({ field: "examples", code: "missing", key });
    }
  }

  if (hasInvalidQuickReplyGrouping(state.buttons)) {
    issues.push({ field: "buttons", code: "interleaved" });
  }

  const quickReplyTextCounts = new Map<string, number>();
  for (const button of state.buttons) {
    if (button.type !== "QUICK_REPLY") continue;
    const key = button.text.trim().toLowerCase();
    if (!key) continue;
    quickReplyTextCounts.set(key, (quickReplyTextCounts.get(key) ?? 0) + 1);
  }
  if ([...quickReplyTextCounts.values()].some((count) => count > 1)) {
    issues.push({ field: "buttons", code: "duplicateQuickReply" });
  }

  state.buttons.forEach((button, buttonIndex) => {
    if (button.type === "PHONE_NUMBER") {
      if (!isValidWhatsAppTemplatePhoneNumber(button.phoneNumber)) {
        issues.push({ field: "buttons", code: "invalidPhone", buttonIndex });
      }
      return;
    }
    if (button.type === "URL") {
      const trimmed = button.url.trim();
      if (!trimmed) {
        issues.push({ field: "buttons", code: "invalidUrl", buttonIndex });
        return;
      }
      const staticBase = trimmed
        .replace(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}$/, "")
        .replace(/\/+$/, "");
      try {
        const parsed = new URL(staticBase);
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
          issues.push({ field: "buttons", code: "invalidUrl", buttonIndex });
        }
      } catch {
        issues.push({ field: "buttons", code: "invalidUrl", buttonIndex });
      }
    }
  });

  return issues;
}

function hasInvalidQuickReplyGrouping(
  buttons: readonly { type: string }[],
): boolean {
  let firstQuickReply = -1;
  let lastQuickReply = -1;
  for (let index = 0; index < buttons.length; index += 1) {
    if (buttons[index]!.type !== "QUICK_REPLY") continue;
    if (firstQuickReply === -1) firstQuickReply = index;
    lastQuickReply = index;
  }
  if (firstQuickReply === -1) return false;

  for (let index = firstQuickReply; index <= lastQuickReply; index += 1) {
    if (buttons[index]!.type !== "QUICK_REPLY") return true;
  }

  return firstQuickReply !== 0 && lastQuickReply !== buttons.length - 1;
}

function buildExamples(state: TemplateFormState): WhatsAppTemplateExamplesInput | undefined {
  const { headerKeys, bodyKeys, urlButtonKeys } = discoverFormVariables(state);
  const examples: WhatsAppTemplateExamplesInput = {};

  if (headerKeys.length === 1 && state.headerExample.trim()) {
    examples.header = state.headerExample.trim();
  }

  if (bodyKeys.length > 0) {
    if (state.parameterFormat === "POSITIONAL") {
      examples.body = bodyKeys.map((key) => state.bodyExamples[key]?.trim() ?? "");
    } else {
      const named: Record<string, string> = {};
      for (const key of bodyKeys) {
        named[key] = state.bodyExamples[key]?.trim() ?? "";
      }
      examples.body = named;
    }
  }

  const urlButtons = urlButtonKeys
    .filter((entry) => entry.keys.length === 1)
    .map((entry) => ({
      buttonIndex: entry.buttonIndex,
      value: state.urlButtonExamples[entry.buttonIndex]?.trim() ?? "",
    }))
    .filter((entry) => entry.value.length > 0);

  if (urlButtons.length > 0) {
    examples.urlButtons = urlButtons;
  }

  if (
    examples.header === undefined &&
    examples.body === undefined &&
    examples.urlButtons === undefined
  ) {
    return undefined;
  }

  return examples;
}

function buildComponents(state: TemplateFormState): WhatsAppTemplateComponentsInput {
  const components: WhatsAppTemplateComponentsInput = {
    body: { text: state.bodyText.trim() },
    // Always include buttons (possibly empty). Omitting the key is not equivalent
    // to "unchanged" on the API — Meta edit replaces the full component set.
    buttons: state.buttons.map((button, buttonIndex) =>
      buildButtonInput(button, buttonIndex, state.urlButtonExamples),
    ),
  };

  if (state.headerKind === "IMAGE") {
    components.header = { format: "IMAGE" };
  } else if (state.hasHeader && state.headerText.trim()) {
    const header: NonNullable<WhatsAppTemplateComponentsInput["header"]> = {
      text: state.headerText.trim(),
    };
    const headerKeys = extractPlaceholderKeys(state.headerText);
    if (headerKeys.length === 1 && state.headerExample.trim()) {
      header.example = state.headerExample.trim();
    }
    components.header = header;
  } else {
    components.header = null;
  }

  if (state.hasFooter && state.footerText.trim()) {
    components.footer = { text: state.footerText.trim() };
  } else {
    components.footer = null;
  }

  return components;
}

function buildButtonInput(
  button: TemplateButtonFormState,
  buttonIndex: number,
  urlButtonExamples: Record<number, string>,
): WhatsAppTemplateButtonInput {
  switch (button.type) {
    case "QUICK_REPLY":
      return { type: "QUICK_REPLY", text: button.text.trim() };
    case "PHONE_NUMBER":
      return {
        type: "PHONE_NUMBER",
        text: button.text.trim(),
        phoneNumber: button.phoneNumber.trim(),
      };
    case "VOICE_CALL":
      return button.ttlMinutes != null
        ? {
            type: "VOICE_CALL",
            text: button.text.trim(),
            ttlMinutes: button.ttlMinutes,
          }
        : { type: "VOICE_CALL", text: button.text.trim() };
    case "URL": {
      const built: WhatsAppTemplateButtonInput = {
        type: "URL",
        text: button.text.trim(),
        url: button.url.trim(),
      };
      const keys = extractPlaceholderKeys(button.url);
      if (keys.length === 1) {
        const example =
          button.example?.trim() || urlButtonExamples[buttonIndex]?.trim();
        if (example) {
          return { ...built, example };
        }
      }
      return built;
    }
    default: {
      const _exhaustive: never = button;
      return _exhaustive;
    }
  }
}

export function buildCreateTemplateRequest(
  state: TemplateFormState,
): CreateWhatsAppTemplateRequest {
  const pruned = pruneStaleExamples(state);
  return {
    name: pruned.name.trim(),
    language: pruned.language,
    category: pruned.category,
    parameterFormat: pruned.parameterFormat,
    components: buildComponents(pruned),
    examples: buildExamples(pruned),
  };
}

export function buildEditTemplateRequest(
  state: TemplateFormState,
  options: { includeCategory: boolean },
): EditWhatsAppTemplateRequest {
  const pruned = pruneStaleExamples(state);
  const request: EditWhatsAppTemplateRequest = {
    parameterFormat: pruned.parameterFormat,
    components: buildComponents(pruned),
    examples: buildExamples(pruned),
  };
  if (options.includeCategory) {
    request.category = pruned.category;
  }
  return request;
}

export function templateDtoToFormState(template: WhatsAppTemplateDto): TemplateFormState {
  const state = createEmptyTemplateFormState();
  state.name = template.name;
  state.language = template.language;
  state.category =
    template.category.trim().toUpperCase() === "MARKETING" ? "MARKETING" : "UTILITY";

  if (!Array.isArray(template.components)) {
    return state;
  }

  let inferredFormat: WhatsAppTemplateManageParameterFormat = "POSITIONAL";
  const allKeys: string[] = [];

  for (const entry of template.components) {
    if (!entry || typeof entry !== "object") continue;
    const component = entry as Record<string, unknown>;
    const type = typeof component.type === "string" ? component.type.toUpperCase() : "";

    if (type === "HEADER") {
      const format =
        typeof component.format === "string" ? component.format.trim().toUpperCase() : "TEXT";
      if (format === "IMAGE") {
        state.hasHeader = true;
        state.headerKind = "IMAGE";
        state.hasStoredHeaderImage = template.headerMedia != null;
      } else if (typeof component.text === "string") {
        state.hasHeader = true;
        state.headerKind = "TEXT";
        state.headerText = component.text;
        allKeys.push(...extractPlaceholderKeys(component.text));
        const example = readHeaderExample(component.example);
        if (example) state.headerExample = example;
      }
    }

    if (type === "BODY" && typeof component.text === "string") {
      state.bodyText = component.text;
      const bodyKeys = extractPlaceholderKeys(component.text);
      allKeys.push(...bodyKeys);
      assignBodyExamples(state, bodyKeys, component.example);
    }

    if (type === "FOOTER" && typeof component.text === "string") {
      state.hasFooter = true;
      state.footerText = component.text;
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      state.buttons = component.buttons
        .map((buttonEntry, buttonIndex) => parseButton(buttonEntry, buttonIndex, state))
        .filter((button): button is TemplateButtonFormState => button !== null);
    }
  }

  const classification = classifyPlaceholderKeys(allKeys);
  if (classification === "NAMED") {
    inferredFormat = "NAMED";
  }
  state.parameterFormat = inferredFormat;

  return pruneStaleExamples(state);
}

function parseButton(
  buttonEntry: unknown,
  buttonIndex: number,
  state: TemplateFormState,
): TemplateButtonFormState | null {
  if (!buttonEntry || typeof buttonEntry !== "object") return null;
  const button = buttonEntry as Record<string, unknown>;
  const type = typeof button.type === "string" ? button.type.toUpperCase() : "";
  const text = typeof button.text === "string" ? button.text : "";

  switch (type) {
    case "QUICK_REPLY":
      return { type: "QUICK_REPLY", clientId: createTemplateFormButtonClientId(), text };
    case "PHONE_NUMBER":
    case "PHONE": {
      const raw =
        typeof button.phone_number === "string"
          ? button.phone_number.trim()
          : typeof button.phoneNumber === "string"
            ? button.phoneNumber.trim()
            : "";
      // Meta stores digits-only; present international form in the editor.
      const phoneNumber =
        raw.length === 0 ? "" : raw.startsWith("+") ? raw : `+${raw}`;
      return {
        type: "PHONE_NUMBER",
        clientId: createTemplateFormButtonClientId(),
        text,
        phoneNumber,
      };
    }
    case "VOICE_CALL": {
      const ttlRaw =
        typeof button.ttl_minutes === "number"
          ? button.ttl_minutes
          : typeof button.ttlMinutes === "number"
            ? button.ttlMinutes
            : 10080;
      return {
        type: "VOICE_CALL",
        clientId: createTemplateFormButtonClientId(),
        text,
        ttlMinutes: ttlRaw,
      };
    }
    case "URL": {
      const url = typeof button.url === "string" ? button.url : "";
      const example = readUrlButtonExample(button.example);
      if (example) state.urlButtonExamples[buttonIndex] = example;
      return {
        type: "URL",
        clientId: createTemplateFormButtonClientId(),
        text,
        url,
        ...(example ? { example } : {}),
      };
    }
    default:
      return null;
  }
}

function readHeaderExample(example: unknown): string | null {
  if (!example || typeof example !== "object") return null;
  const record = example as Record<string, unknown>;
  if (Array.isArray(record.header_text) && typeof record.header_text[0] === "string") {
    return record.header_text[0];
  }
  if (Array.isArray(record.header_text_named_params)) {
    const first = record.header_text_named_params[0];
    if (first && typeof first === "object" && typeof (first as { example?: unknown }).example === "string") {
      return (first as { example: string }).example;
    }
  }
  return null;
}

function assignBodyExamples(
  state: TemplateFormState,
  bodyKeys: readonly string[],
  example: unknown,
): void {
  if (!example || typeof example !== "object") return;
  const record = example as Record<string, unknown>;

  if (Array.isArray(record.body_text_named_params)) {
    for (const entry of record.body_text_named_params) {
      if (!entry || typeof entry !== "object") continue;
      const param = entry as { param_name?: unknown; example?: unknown };
      if (typeof param.param_name === "string" && typeof param.example === "string") {
        state.bodyExamples[param.param_name] = param.example;
      }
    }
    return;
  }

  if (Array.isArray(record.body_text) && Array.isArray(record.body_text[0])) {
    const values = record.body_text[0] as unknown[];
    bodyKeys.forEach((key, index) => {
      const value = values[index];
      if (typeof value === "string") {
        state.bodyExamples[key] = value;
      }
    });
  }
}

function readUrlButtonExample(example: unknown): string | null {
  if (Array.isArray(example) && typeof example[0] === "string") {
    return example[0];
  }
  return null;
}

export function shouldRemoveTemplateAfterDelete(item: WhatsAppTemplateDto): boolean {
  const status = item.status.trim().toUpperCase();
  return status === "DELETED";
}

export function shouldKeepTemplateAfterDelete(item: WhatsAppTemplateDto): boolean {
  const status = item.status.trim().toUpperCase();
  return status === "PENDING_DELETION";
}

/**
 * Live Create/Edit preview model using example values for variables.
 * Display-only — not used for provider payloads.
 */
export function buildFormTemplatePresentation(
  state: TemplateFormState,
): WhatsAppTemplatePresentation {
  const pruned = pruneStaleExamples(state);
  const { headerKeys, bodyKeys, urlButtonKeys } = discoverFormVariables(pruned);

  const headerText =
    pruned.headerKind !== "IMAGE" && pruned.hasHeader && pruned.headerText.trim()
      ? substituteWithExamples(pruned.headerText, headerKeys, (key) =>
          key === headerKeys[0] ? pruned.headerExample : "",
        )
      : null;

  const bodySource = pruned.bodyText.trim() || " ";
  const bodyText = substituteWithExamples(bodySource, bodyKeys, (key) =>
    pruned.bodyExamples[key] ?? "",
  );

  const footerText =
    pruned.hasFooter && pruned.footerText.trim() ? pruned.footerText.trim() : null;

  const buttons = pruned.buttons.map((button, buttonIndex) => {
    if (button.type === "QUICK_REPLY") {
      return {
        type: "QUICK_REPLY" as const,
        text: button.text.trim() || "…",
      };
    }
    if (button.type === "VOICE_CALL") {
      return { type: "VOICE_CALL" as const, text: button.text.trim() || "…" };
    }
    if (button.type === "PHONE_NUMBER") {
      return {
        type: "PHONE_NUMBER" as const,
        text: button.text.trim() || "…",
        ...(button.phoneNumber.trim()
          ? { phoneNumber: button.phoneNumber.trim() }
          : {}),
      };
    }
    const urlKeys = urlButtonKeys.find((entry) => entry.buttonIndex === buttonIndex)?.keys ?? [];
    const example = pruned.urlButtonExamples[buttonIndex] ?? "";
    const url = substituteWithExamples(button.url, urlKeys, () => example);
    return {
      type: "URL" as const,
      text: button.text.trim() || "…",
      ...(url.trim() ? { url: url.trim() } : {}),
    };
  });

  return {
    ...(pruned.headerKind === "IMAGE"
      ? {
          header: {
            type: "IMAGE" as const,
            ...(pruned.headerImagePreviewUrl
              ? { imageUrl: pruned.headerImagePreviewUrl }
              : {}),
            missing: !pruned.headerImagePreviewUrl,
            ...(pruned.headerImageFile ? { fileName: pruned.headerImageFile.name } : {}),
          },
        }
      : headerText
        ? { header: { type: "TEXT" as const, text: headerText } }
        : {}),
    body: { text: bodyText.trim().length > 0 ? bodyText : "…" },
    ...(footerText ? { footer: { text: footerText } } : {}),
    buttons,
  };
}

function substituteWithExamples(
  text: string,
  keys: readonly string[],
  exampleForKey: (key: string) => string,
): string {
  let resolved = text;
  for (const key of keys) {
    const example = exampleForKey(key).trim();
    const replacement = example.length > 0 ? example : `{{${key}}}`;
    const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, "g");
    resolved = resolved.replace(pattern, replacement);
  }
  return resolved;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
